import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API_URL from '../../config/api';
import { X, Plus, Edit2, Trash2, AlertTriangle, ChevronDown, ChevronUp, PackageOpen } from 'lucide-react';

// ⭐ Gestión de lotes de un producto con manejo de caducidad (PEPS/FIFO).
// Se abre desde ProductosManager cuando el producto tiene track_batches
// activado. Cada operación (agregar/ajustar/retirar) mantiene sincronizado
// products.stock en el backend; aquí solo se refleja el total resultante
// vía onStockChange para que el formulario de producto no quede desfasado.
function formatDate(value) {
  if (!value) return 'Sin fecha';
  const d = new Date(value + 'T00:00:00');
  if (isNaN(d.getTime())) return value;
  return d.toLocaleDateString('es-EC', { day: '2-digit', month: 'short', year: 'numeric' });
}

function daysUntil(value) {
  if (!value) return null;
  const target = new Date(value + 'T00:00:00');
  if (isNaN(target.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((target - today) / (1000 * 60 * 60 * 24));
}

function urgencyStyle(days) {
  if (days === null) return 'bg-gray-100 text-gray-600';
  if (days < 0) return 'bg-red-100 text-red-700';
  if (days <= 7) return 'bg-orange-100 text-orange-700';
  if (days <= 30) return 'bg-yellow-100 text-yellow-700';
  return 'bg-green-100 text-green-700';
}

function urgencyLabel(days) {
  if (days === null) return 'Sin fecha';
  if (days < 0) return `Vencido hace ${Math.abs(days)}d`;
  if (days === 0) return 'Vence hoy';
  return `Vence en ${days}d`;
}

export function GestionarLotesModal({ token, product, onClose, onStockChange }) {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const [newQty, setNewQty] = useState('');
  const [newExpiration, setNewExpiration] = useState('');
  const [newNotes, setNewNotes] = useState('');

  const [editingId, setEditingId] = useState(null);
  const [editQty, setEditQty] = useState('');
  const [editExpiration, setEditExpiration] = useState('');

  const [confirmRemove, setConfirmRemove] = useState(null);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/pos/products/${product.id}/batches`, authHeaders);
      setBatches(res.data || []);
    } catch (err) {
      setError('No se pudieron cargar los lotes.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.id, token]);

  useEffect(() => { load(); }, [load]);

  const total = batches.reduce((sum, b) => sum + Number(b.quantity), 0);

  const handleAdd = async () => {
    const qty = parseInt(newQty, 10);
    if (isNaN(qty) || qty <= 0) {
      setError('Ingresa una cantidad válida.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await axios.post(
        `${API_URL}/api/pos/products/${product.id}/batches`,
        { quantity: qty, expiration_date: newExpiration || null, notes: newNotes.trim() },
        authHeaders
      );
      setNewQty(''); setNewExpiration(''); setNewNotes('');
      await load();
      onStockChange?.(total + qty);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo registrar el lote.');
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (batch) => {
    setEditingId(batch.id);
    setEditQty(String(batch.quantity));
    setEditExpiration(batch.expiration_date || '');
  };

  const handleSaveEdit = async (batchId) => {
    const qty = parseInt(editQty, 10);
    if (isNaN(qty) || qty < 0) {
      setError('La cantidad debe ser un número válido.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await axios.put(
        `${API_URL}/api/pos/batches/${batchId}`,
        { quantity: qty, expiration_date: editExpiration || null },
        authHeaders
      );
      setEditingId(null);
      await load();
      onStockChange?.(null); // el padre recarga el producto completo via loadProducts
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo ajustar el lote.');
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async (batchId) => {
    setConfirmRemove(null);
    setBusy(true);
    setError('');
    try {
      await axios.delete(`${API_URL}/api/pos/batches/${batchId}`, authHeaders);
      await load();
      onStockChange?.(null);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo retirar el lote.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[75] p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2">
            <PackageOpen className="w-5 h-5 text-blue-600" /> Lotes de {product.name}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-300 text-red-700 text-sm rounded-lg p-3">{error}</div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
            Stock total (suma de lotes activos): <span className="font-bold">{total}</span>
          </div>

          {/* Alta de nuevo lote */}
          <div className="border rounded-lg p-3 space-y-2">
            <p className="text-sm font-semibold text-gray-700">Registrar entrada de mercancía</p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Cantidad</label>
                <input type="number" min="1" value={newQty} onChange={(e) => setNewQty(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="0" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Fecha de caducidad</label>
                <input type="date" value={newExpiration} onChange={(e) => setNewExpiration(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm" />
              </div>
            </div>
            <input type="text" value={newNotes} onChange={(e) => setNewNotes(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Notas (opcional)" />
            <button onClick={handleAdd} disabled={busy}
              className="w-full py-2 bg-blue-600 text-white font-semibold rounded-lg text-sm hover:bg-blue-700 disabled:opacity-60 flex items-center justify-center gap-1.5">
              <Plus className="w-4 h-4" /> Agregar Lote
            </button>
          </div>

          {/* Lista de lotes */}
          <div>
            <p className="text-sm font-semibold text-gray-700 mb-2">Lotes activos {loading && '(cargando...)'}</p>
            {!loading && batches.length === 0 && (
              <p className="text-sm text-gray-400 text-center py-4">Sin lotes registrados todavía.</p>
            )}
            <div className="space-y-2">
              {batches.map((b) => {
                const days = daysUntil(b.expiration_date);
                const isEditing = editingId === b.id;
                return (
                  <div key={b.id} className="border rounded-lg p-3">
                    {isEditing ? (
                      <div className="space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Cantidad</label>
                            <input type="number" min="0" value={editQty} onChange={(e) => setEditQty(e.target.value)}
                              className="w-full px-2 py-1.5 border rounded text-sm" />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Fecha de caducidad</label>
                            <input type="date" value={editExpiration} onChange={(e) => setEditExpiration(e.target.value)}
                              className="w-full px-2 py-1.5 border rounded text-sm" />
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => setEditingId(null)} className="flex-1 py-1.5 border rounded text-sm text-gray-600 hover:bg-gray-50">Cancelar</button>
                          <button onClick={() => handleSaveEdit(b.id)} disabled={busy} className="flex-1 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-60">Guardar ajuste</button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-semibold text-gray-800">{b.quantity} unidades</p>
                          <p className="text-xs text-gray-500">{formatDate(b.expiration_date)}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-bold px-2 py-1 rounded-full ${urgencyStyle(days)}`}>
                            {urgencyLabel(days)}
                          </span>
                          <button onClick={() => startEdit(b)} className="p-1.5 text-gray-400 hover:text-blue-600" title="Ajustar (auditoría)">
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button onClick={() => setConfirmRemove(b)} className="p-1.5 text-gray-400 hover:text-red-600" title="Retirar lote">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {confirmRemove && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[80] p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6">
              <h4 className="font-bold text-gray-900 mb-2">Retirar lote</h4>
              <p className="text-sm text-gray-600 mb-4">
                ¿Retirar el lote de {confirmRemove.quantity} unidades ({formatDate(confirmRemove.expiration_date)})? Se descontará del stock total. Úsalo cuando encuentres producto vencido o dañado que ya no está disponible para vender.
              </p>
              <div className="flex gap-3">
                <button onClick={() => setConfirmRemove(null)} className="flex-1 py-2 border rounded-lg text-gray-700 hover:bg-gray-50">Cancelar</button>
                <button onClick={() => handleRemove(confirmRemove.id)} className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Retirar</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const DAY_WINDOWS = [7, 15, 30, 60];

export function ProductosPorVencerPanel({ token }) {
  const [days, setDays] = useState(30);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/pos/batches/expiring`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { days }
      });
      setItems(res.data || []);
    } catch (err) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [token, days]);

  useEffect(() => { load(); }, [load]);

  const vencidos = items.filter(it => daysUntil(it.expiration_date) < 0).length;

  if (!loading && items.length === 0 && !expanded) {
    return null; // sin nada por vencer: no ensucia la pantalla
  }

  return (
    <div className="mb-4 border rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(v => !v)}
        className={`w-full flex items-center justify-between px-4 py-3 text-left ${
          vencidos > 0 ? 'bg-red-50' : items.length > 0 ? 'bg-yellow-50' : 'bg-gray-50'
        }`}
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-gray-800">
          <AlertTriangle className={`w-4 h-4 ${vencidos > 0 ? 'text-red-600' : 'text-yellow-600'}`} />
          {loading ? 'Revisando productos por vencer...' : items.length === 0
            ? 'Sin productos por vencer'
            : `${items.length} lote${items.length !== 1 ? 's' : ''} por vencer${vencidos > 0 ? ` (${vencidos} ya vencido${vencidos !== 1 ? 's' : ''})` : ''}`}
        </span>
        {expanded ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
      </button>

      {expanded && (
        <div className="p-3 border-t space-y-2">
          <div className="flex gap-2">
            {DAY_WINDOWS.map(d => (
              <button key={d} onClick={() => setDays(d)}
                className={`px-3 py-1 rounded-full text-xs font-semibold ${days === d ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                {d} días
              </button>
            ))}
          </div>
          {items.length === 0 ? (
            <p className="text-sm text-gray-400 py-2">Ningún lote vence dentro de {days} días.</p>
          ) : (
            <div className="max-h-56 overflow-y-auto divide-y">
              {items.map((it) => {
                const d = daysUntil(it.expiration_date);
                return (
                  <div key={it.id} className="flex items-center justify-between py-2 text-sm">
                    <div>
                      <p className="font-semibold text-gray-800">{it.product_name}</p>
                      <p className="text-xs text-gray-500">{it.quantity} unidades · {formatDate(it.expiration_date)}</p>
                    </div>
                    <span className={`text-xs font-bold px-2 py-1 rounded-full ${urgencyStyle(d)}`}>{urgencyLabel(d)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
